import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

// PR-CHIPS MAY 11 — Sifi-style upload status chips for list views.
//   uploaded     → emerald, click → jump to upload section
//   pending      → amber pulsing, click → jump to upload section
//   not_required → grey, no click
//
// The "click" handler is wired through to the parent so each list page can
// navigate to its own detail URL with a ?focus=<chipKey> query param, which
// the detail page scrolls to (see useFocusScroll).

export type UploadChipStatus = 'uploaded' | 'pending' | 'not_required';

export interface UploadStatusChipProps {
  status: UploadChipStatus;
  icon: LucideIcon;
  /** Tooltip / a11y text. Shown on hover. */
  label: string;
  /** Optional handler invoked when an uploaded/pending chip is clicked. */
  onClick?: () => void;
}

export function UploadStatusChip({ status, icon: Icon, label, onClick }: UploadStatusChipProps) {
  const tooltip =
    status === 'uploaded'     ? `${label}: uploaded ✓`
    : status === 'pending'    ? `${label}: pending — click to upload`
    : `${label}: not required`;

  const base = 'inline-flex items-center justify-center w-6 h-6 rounded-full transition';
  if (status === 'not_required') {
    return (
      <span
        className={cn(base, 'bg-muted/40 opacity-40 cursor-default')}
        title={tooltip}
        aria-label={tooltip}
      >
        <Icon className="w-3 h-3 text-muted-foreground" />
      </span>
    );
  }

  const variant = status === 'uploaded'
    ? 'bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:hover:bg-emerald-900/60'
    : 'bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/40 dark:hover:bg-amber-900/60 animate-pulse';
  const iconColor = status === 'uploaded'
    ? 'text-emerald-700 dark:text-emerald-300'
    : 'text-amber-700 dark:text-amber-300';

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      className={cn(base, variant, 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring')}
      title={tooltip}
      aria-label={tooltip}
    >
      <Icon className={cn('w-3 h-3', iconColor)} />
    </button>
  );
}
